import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchData } from "../utils";
import * as endpoints from '../settings';
import { schemas } from './schemas';
import { setNotification } from "./notificationsSlice";
import { toLower, toUpper } from "lodash";

const dataUrls = {
    organisations: endpoints.API_ORGS_ALL,
    users: endpoints.API_ORG_USERS,
    issues: endpoints.API_SUPPORT_ISSUES,
    contracts: endpoints.API_ORGS_CONTRACTS,
    packages: endpoints.API_PACKAGES
};

const manageDataUrls = {
    contracts: endpoints.API_CONTRACTS
};

const manageDataSchemas = {
    contracts: schemas.MANAGE_CONTRACTS
};

export const getData = createAsyncThunk(
    "getData",
    async({ tab, manage }, { dispatch, rejectWithValue, getState }) => {
        const tabs = ["contracts", "packages", "users", "organisations"], isPutRequest = tabs.includes(tab);
        try {
            let url = manage ? manageDataUrls[tab] : dataUrls[tab], headers = isPutRequest ? { "Content-Type": "application/json" } : {},
            schema = manage ? manageDataSchemas[tab] : schemas[tab] || {};
            const { filterOptions } = getState().data;
            let body = {};
            filterOptions.map(o => body[o.key] = o.value);
            if (url) {
                const response = await fetchData(url, {
                    method: isPutRequest && !manage ? "PUT" : "GET",
                    headers: headers,
                    body: isPutRequest && !manage ? JSON.stringify(body) : undefined
                }, schema);
                return schema === {}
                    ? Object.values(response.result)
                    : response && response.entities && response.entities[tab]
                        ? Object.values(response.entities[tab])
                        : [];
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

const auditDataUrls = {
    download: endpoints.API_AUDIT_PACKAGES_DOWNLOAD
};

export const getAuditData = createAsyncThunk(
    "getAuditData",
    async({ tab }, { dispatch, rejectWithValue, getState }) => {
        try {
            const { currentPage, context, filterOptions } = getState().data;
            let url = auditDataUrls[tab],
            body = {
                contextsearch: context,
                limit: endpoints.PAGE_SIZE,
                offset: (currentPage - 1) * endpoints.PAGE_SIZE
            };
            filterOptions.map(o => body[o.key] = o.value);
            if (url) {
                const response = await fetchData(url, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body)
                }, {});
                return response.result;
            }
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);    }
)
const detailsSchemas = {
    organisations: {
        url: endpoints.API_ORG_DETAILS,
        schema: schemas.ORG,
        users: {
            url: endpoints.API_ORG_DETAILS + "/users",
            schema: schemas.ORG_USERS
        },
        issues: { url: endpoints.API_ORG_DETAILS + "/issues", schema: schemas.ORG_ISSUES }
    },
    users: {
        url: endpoints.API_USER_DETAILS,
        schema: schemas.USER
    },
    issues: {
        url: endpoints.API_SUPPORT_ISSUE,
        schema: schemas.ISSUE
    },
    contracts: {
        url: endpoints.API_CONTRACT,
        schema: schemas.MANAGE_CONTRACTS
    },
    packages: {
        url: endpoints.API_PACKAGE,
        schema: schemas.PACKAGE
    },
    audit: {
        download: {
            url: endpoints.API_AUDIT_PACKAGES_DOWNLOAD + "/details",
            schema: schemas.AUDIT_DETAILS
        }
    }
};

export const getDetails = createAsyncThunk(
    "getDetails",
    async({ page, org, tab, selected }, { dispatch, rejectWithValue }) => {
        try {
            let url = null, headers = {}, schema = {};
            if (Object.keys(detailsSchemas).indexOf(page) > -1) {
                if (tab) {
                    if (Object.keys(detailsSchemas[page]).indexOf(tab) > -1) {
                        url = detailsSchemas[page][tab].url;
                        schema = detailsSchemas[page][tab].schema;
                    };
                } else {
                    url = detailsSchemas[page].url;
                    schema = detailsSchemas[page].schema;
                };
                if (org) headers["X-Org-Alias"] = toLower(org);
                if (page === "users") headers["X-User-Email"] = toLower(selected);
                if (page === "issues") headers["X-Ticket-Ref"] = toUpper(selected);
                if (page === "contracts") headers["X-Contract-Name"] = selected;
                if (page === "packages") headers["X-Pkg-Id"] = selected;
                if (page === "audit") headers["X-Audit-Number"] = selected;
            };
            if (url) {
                const response = await fetchData(url, {
                    headers: headers
                }, schema);
                 if (response && selected && (page === "issues" || tab === "issues")) {
                    dispatch(getIssueComments(selected));
                    dispatch(getIssueAttachments(selected));
                }
                if (tab && org) {
                    let details = Object.assign({}, response.entities[tab][org]);
                    Object.keys(details).map(key => {
                        if (Array.isArray(details[key])) delete details[key];
                    });
                    return({
                        details: details,
                        data: response.entities[tab][org][tab]
                    })
                } else {
                    return({ details: response.entities[page][toLower(selected)] || {}, data: [] });
                };
            };
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({ code: e.code });
        };
        return rejectWithValue({});
    }
);

const getIssueComments = createAsyncThunk(
    "getIssueComments",
    async(issue, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_SUPPORT_ISSUE + "/comments", {
                headers: {
                    "X-Ticket-Ref": issue
                }
            }, schemas.ISSUE_COMMENTS);
            return response.entities.hasOwnProperty("comments")
                ?  Object.values(response.entities.comments)
                : [];
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

const getIssueAttachments = createAsyncThunk(
    "getIssueAttachments",
    async(issue, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_SUPPORT_ISSUE + "/attachments", {
                headers: {
                    "X-Ticket-Ref": issue
                }
            }, {});
            return response.hasOwnProperty("result")
                ?  Object.values(response.result)
                : [];
        } catch (e) {
            dispatch(setNotification(e));
        };
        return rejectWithValue([]);
    }
);

export const downloadAttachment = createAsyncThunk(
    "downloadAttachment",
    async({ issue, id }, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(endpoints.API_SUPPORT_ISSUE_ATTACHMENT + "/download", {
                headers: {
                    "X-Ticket-Ref": issue,
                    "X-Attachment-Id": id
                }
            }, {});
            if (response.result && response.result.url) {
                let a = document.createElement("a");
                a.href = response.result.url;
                a.rel = "noopener noreferrer";
                a.click();
                a.remove();
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const deleteAttachment = createAsyncThunk(
    "deleteAttachment",
    async({ issue, id }, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_SUPPORT_ISSUE_ATTACHMENT, {
                method: "delete",
                headers: {
                    "X-Ticket-Ref": issue,
                    "X-Attachment-Id": id
                }
            }, {});
            if (response.result && response.result.success) {
                dispatch(getIssueAttachments(issue));
            };
            dispatch(setNotification(response.result));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const updateAttachment = createAsyncThunk(
    "updateAttachment",
    async({ issue, id, visible }, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_SUPPORT_ISSUE_ATTACHMENT + "/update", {
                method: "put",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    "ticket_ref": issue,
                    "attachment_id": id,
                    "visible": visible
                })
            }, {});
            if (response.result && response.result.success) {
                dispatch(getIssueAttachments(issue));
            };
            dispatch(setNotification(response.result));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const saveIssueComment = createAsyncThunk(
    "saveIssueComment",
    async({ issue, comment, files=[], visible }, { dispatch }) => {
        try {
            const fmData = new FormData();
            fmData.append("data", JSON.stringify({
                issue: issue,
                comment: comment ? comment : undefined,
                visible: visible
            }));
            for (const file of files) {
                fmData.append("attachment", file);
            };
            const response = await fetchData(endpoints.API_SUPPORT_ISSUE + "/update", {
                method: "put",
                body: fmData
            });
            if (files.length) {
                dispatch(getIssueAttachments(issue));
            };
            if (comment) {
                dispatch(getIssueComments(issue));
            }
            dispatch(setNotification(response.result));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const deleteUser = createAsyncThunk(
    "deleteUser",
    async(email, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_USER_DETAILS, {
                method: "delete",
                headers: { "X-User-Email": email }
            }, {});
            dispatch(setNotification(response.result));
            let path = window.location.href,
            idx = path.lastIndexOf(email);
            if (idx > -1) {
                window.location.replace(path.substring(0, idx - 1));
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const revokeGrant = createAsyncThunk(
    "revokeGrant",
    async({ email, alias }, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_ISSUE_GRANT, {
                method: "delete",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: email,
                    org_alias: alias
                })
            }, {});
            dispatch(getDetails({ page: "users", selected: email }));
            dispatch(setNotification(response.result));
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const changeUserAccess = createAsyncThunk(
    "changeUserAccess",
    async({ email, disabled }, { dispatch }) => {
        try {
            const response = await fetchData(disabled ? endpoints.API_ACTIVATE_USER : endpoints.API_DISABLE_USER, {
                method: "PUT",
                headers: {
                    "X-User-Email": email
                }
            }, {});
            dispatch(getDetails({ page: "users", selected: email }));
            dispatch(setNotification(response.result));
        } catch (e) {
            dispatch(setNotification(e));
        }
    }
)

export const verifyPackage = createAsyncThunk(
    "verifyPkg",
    async({ pkgId, verify }, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_VERIFY_PACKAGE, {
                method: "put",
                headers: {
                    "X-Pkg-Id": pkgId,
                    "X-Pkg-Verify": verify
                }
            }, {});
            dispatch(getDetails({ page: "packages", selected: pkgId }));
            dispatch(setNotification(response.result))
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const deletePackage = createAsyncThunk(
    "deletePkg",
    async(pkgId, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_PACKAGE, {
                method: "delete",
                headers: {
                    "X-Pkg-Id": pkgId
                }
            }, {});
            dispatch(setNotification(response.result));
            let path = window.location.href,
            idx = path.lastIndexOf(pkgId);
            if (idx > -1) {
                window.location.replace(path.substring(0, idx - 1));
            }
            else if (path.indexOf("packages") > -1) {
                dispatch(getData({ tab: "packages" }));
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

const reportUrls = {
    contracts: endpoints.API_CONTRACTS_REPORT,
    packages: endpoints.API_PACKAGES_REPORT,
    users: endpoints.API_USERS_REPORT,
    organisations: endpoints.API_ORGS_REPORT,
    download: endpoints.API_AUDIT_PACKAGES_DOWNLOAD_REPORT
};


export const getReport = createAsyncThunk(
    "getReport",
    async({ tab, format }, { dispatch, getState }) => {
        try {
            const { filterOptions } = getState().data;
            let body = { "report_format": format };
            filterOptions.map(o => body[o.key] = o.value);
            const url = reportUrls[tab];

            if (url) {
                const response = await fetchData(url, {
                    method: "put",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(body)
                }, {});
                if (response.result && response.result.hasOwnProperty("url")) {
                    window.open(response.result.url);
                }
            } else {
                dispatch(setNotification("Not implemented."));
            }
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const activateContract = createAsyncThunk(
    "activateContract",
    async({ contract, activeFlag }, { dispatch }) =>{
        try {
            const response = await fetchData(`${endpoints.API_CONTRACT}/${activeFlag ? "activate" : "deactivate"}`, {
                method: "PUT",
                headers: {
                    "X-Contract-Name": contract
                }
            }, {});
            dispatch(setNotification(response.result));
            let href = window.location.href;
            if (href.indexOf("contracts") > -1 && href.indexOf(contract) > -1) {
                dispatch(getDetails({ page: "contracts",  selected: contract }));
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

export const deleteOrgContract = createAsyncThunk(
    "deleteOrgCOntract",
    async({ alias, contract }, { dispatch }) => {
        try {
            const response = await fetchData(endpoints.API_ORG_CONTRACT, {
                method: "delete",
                headers: {
                    "X-Org-Alias": alias,
                    "X-Contract-Name": contract
                }
            }, {});
            dispatch(setNotification(response.result));
            let href = window.location.href;
            if (href.indexOf("contracts") > -1) {
                if (href.indexOf(contract) > -1) {
                    dispatch(getDetails({ page: "contracts",  selected: contract }));
                } else {
                    dispatch(getData({ tab: "contracts" }));
                };
            } else if (href.indexOf(alias) > -1) {
                dispatch(getDetails({ page: "organisations", org: alias, selected: alias }))
            };
        } catch (e) {
            dispatch(setNotification(e));
        };
    }
);

const initialState = {
    data: [],
    details: {},
    loading: false,
    context: "",
    search: false,
    notFound: false,
    tab: null,
    sortColumn: null,
    sortOrder: null,
    filterOptions: [],
    currentPage: 1,
    total: 0
};

const dataSlice = createSlice({
    name: 'data',
    initialState,
    reducers: {
        filterData: (state, action) => {
            state.context = action.payload;
        },
        expandSearch: (state, action) => {
            state.search = action.payload;
            if (action.payload === false) {
                state.context = "";
                state.filterOptions = [];
            };
        },
        resetData: () => {
            return initialState;
        },
        setTab: (state, action) => {
            if (state.tab !== action.payload.tab || action.payload.tab === "contracts") {
                return {
                    ...initialState,
                    tab: action.payload.tab,
                    sortColumn: action.payload.column,
                    sortOrder: action.payload.order
                };
            }
        },
        sortData: (state, action) => {
            state.sortOrder = action.payload.order;
            state.sortColumn = action.payload.column;
        },
        setFilter: (state, action) => {
            let idx = state.filterOptions.findIndex(o => o.key === action.payload.key);
            state.currentPage = 1;
            if (idx < 0) {
                state.filterOptions.push(action.payload);
            } else {
                delete state.filterOptions.splice(idx, 1);
            }
        },
        setPageNumber: (state, action) => {
            state.currentPage = action.payload;
        }
    },
    extraReducers: {
        [getData.pending]: (state) => {
            state.loading = true;
        },
        [getData.fulfilled]: (state, action) => {
            state.data = action.payload;
            state.details = {};
            state.loading = false;
        },
        [getData.rejected]: (state, action) => {
            return initialState;
        },
        [getAuditData.pending]: (state) => {
            state.loading = true;
        },
        [getAuditData.fulfilled]: (state, action) => {
            state.data = action.payload.result;
            state.total = action.payload.total;
            state.details = {};
            state.loading = false;
        },
        [getAuditData.rejected]: (state, action) => {
            return initialState;
        },
        [getDetails.pending]: (state) => {
            state.loading = true;
        },
        [getDetails.fulfilled]: (state, action) => {
            state.loading = false;
            state.details = action.payload.details;
            state.data = action.payload.data;
            state.notFound = action.payload.code === 404;
        },
        [getDetails.rejected]: (state, action) => {
            state.loading = false;
            state.notFound = action.payload.code === 404;
        },
        [getIssueComments.pending]: (state) => {
            state.details["comments"] = null;
        },
        [getIssueComments.fulfilled]: (state, action) => {
            state.details["comments"] = action.payload;
        },
        [getIssueComments.rejected]: (state) => {
            state.details["comments"] = []
        },
        [getIssueAttachments.pending]: (state) => {
            state.details["attachments"] = null;
        },
        [getIssueAttachments.fulfilled]: (state, action) => {
            state.details["attachments"] = action.payload;
        },
        [getIssueAttachments.rejected]: (state) => {
            state.details["attachments"] = []
        }
    }
});

export const { filterData, expandSearch, resetData, setTab, sortData, setFilter, setPageNumber } = dataSlice.actions;
export default dataSlice.reducer;